import { Path, PathCodec } from "@pomle/paths";
import { useLocation } from "@pomle/react-router-paths";
import { getAndParseLocationCookie } from "lib/nekoCookies";
import { useEffect } from "react";
import { AssignToNoravECGDevice } from "render/views/AssignToNoravECGDevice";
import { AssignToRoomView } from "render/views/AssignToRoom";
import { AssignToSkinScanner } from "render/views/AssignToSkinScanner";
import { AssignVials } from "render/views/AssignVials";
import { CheckInCompleteView } from "render/views/CheckInCompleteView";
import { ChecklistView } from "render/views/ChecklistView";
import { CheckOutConfirmedView } from "render/views/CheckOutConfirmedView";
import { CheckOutConfirmView } from "render/views/CheckOutConfirmView";
import { CheckOutView } from "render/views/CheckOutView";
import { DrawConfirmationView } from "render/views/DrawConfirmationView";
import { ExtendedVisitBloodDrawIntro } from "render/views/ExtendedVisitBloodDrawIntro";
import { ExtendedVisitBloodDrawOutro } from "render/views/ExtendedVisitBloodDrawOutro";
import { ExtendedVisitScanIntro } from "render/views/ExtendedVisitScanIntro";
import { FeatureFlagsView } from "render/views/FeatureFlagsView";
import { FollowUpDermaView } from "render/views/FollowUpDermaView";
import { FollowUpEcgView } from "render/views/FollowUpEcgView";
import { IdentityCheckView } from "render/views/IdentityCheckView";
import { ImmediateRiskAssessmentView } from "render/views/ImmediateRiskAssessmentView";
import { JoinSessionView } from "render/views/JoinSessionView";
import { LocationSelectorView } from "render/views/LocationSelector";
import { ManualCheckInConfirmedView } from "render/views/ManualCheckInConfirmedView";
import { NekoLensAuthView } from "render/views/NekoLensAuthView/NekoLensAuthView";
import { PreferredNameView } from "render/views/PreferredNameView";
import { SelectActiveVisit } from "render/views/SelectActiveVisit";
import { SelectVisitView } from "render/views/SelectVisit";
import { SelectVisitConfirmView } from "render/views/SelectVisitConfirm";
import { SessionJoinCompleteView } from "render/views/SessionJoinCompleteView";
import { StudyCounterSignView } from "render/views/StudyCounterSignView";
import { paths } from "./paths";

function createStep<Codec extends PathCodec>(
  id: string,
  path: Path<Codec>,
  component: (
    params: ReturnType<Path<Codec>["decode"]>,
    options: {
      goTo: (url: string) => void;
      replaceWith: (url: string) => void;
      onBack: () => void;
    }
  ) => React.ReactElement | null
) {
  return {
    id,
    path,
    component,
  };
}

export const steps = [
  createStep(
    "b2e4f6a8-1c3d-5e7f-9b0d-2e4f6a8c0e2f",
    paths.featureFlags,
    (_, { onBack }) => {
      return <FeatureFlagsView onBack={onBack} />;
    }
  ),

  createStep(
    "fe64c95d-dcb4-4d50-8018-1631c345ef3c",
    paths.locationSelector,
    (_, { goTo }) => {
      return <LocationSelectorView goTo={goTo} />;
    }
  ),

  createStep(
    "48316e68-66f4-11ee-a533-00155db6b9f6",
    paths.root,
    (_, { goTo }) => {
      const { pathname } = useLocation();

      /**
       * If the location cookie gets changed, the component will not be automatically
       * re-rendered. But now we don't have flows, where cookie is updated in background,
       * so it's acceptable.
       *
       * Ideally we should listen for `CookieChangeEvent`, but it's only available
       * in Safari 18.4 and later, and as of Apr 2025, large amount of our iPads
       * in clinics are still running iPadOS 18.3. So we keep the behavior consistent
       * across all devices.
       *
       * - https://developer.mozilla.org/en-US/docs/Web/API/CookieChangeEvent
       * - Browser version stats: https://sharedprodcur8grafana-ggh2fkd3bdhcbhea.neu.grafana.azure.com/goto/Ve1kwM1Ng?orgId=1
       */
      const parsedLocation = getAndParseLocationCookie();

      useEffect(() => {
        if (parsedLocation) {
          return;
        }

        const isDirectMatch = paths.locationSelector.match(pathname) === 0;
        if (isDirectMatch) {
          return;
        }

        goTo(paths.locationSelector.url({}));
      }, [goTo, parsedLocation, pathname]);

      return parsedLocation ? (
        <JoinSessionView goTo={goTo} parsedLocation={parsedLocation} />
      ) : null;
    }
  ),

  createStep(
    "8b1584aa-5d01-41a0-a4a1-ea842c6b99ea",
    paths.selectVisit,
    (_, { goTo, onBack }) => {
      return <SelectVisitView goTo={goTo} onBack={onBack} />;
    }
  ),

  createStep(
    "4c5c519c-7659-4c94-ac3a-d0ec4c5668f3",
    paths.selectActiveVisit,
    (_, { goTo, onBack }) => {
      return <SelectActiveVisit goTo={goTo} onBack={onBack} />;
    }
  ),

  createStep(
    "6090e7f4-66f4-11ee-8c44-00155db6b9f6",
    paths.assignSlot,
    (params, { goTo, onBack }) => {
      return (
        <SessionJoinCompleteView
          goTo={goTo}
          onBack={onBack}
          sessionId={params.sessionId}
          siteId={params.siteId}
        />
      );
    }
  ),

  createStep(
    "6654e21c-66f4-11ee-98e4-00155db6b9f6",
    paths.awaitingConsent,
    (params, { goTo, onBack }) => {
      return (
        <ChecklistView
          goTo={goTo}
          onBack={onBack}
          patientId={params.patientId}
          visitId={params.visitId}
        />
      );
    }
  ),

  createStep(
    "c1d2e3f4-a5b6-7890-cdef-1234567890ab",
    paths.identityCheck,
    (params, { goTo, onBack }) => {
      return (
        <IdentityCheckView
          goTo={goTo}
          onBack={onBack}
          patientId={params.patientId}
          visitId={params.visitId}
        />
      );
    }
  ),

  createStep(
    "4c4566e5-213c-46b4-883c-3f38e99440ab",
    paths.selectVisitConfirm,
    (params, { goTo, onBack }) => {
      return (
        <SelectVisitConfirmView
          goTo={goTo}
          onBack={onBack}
          patientId={params.patientId}
          visitId={params.visitId}
        />
      );
    }
  ),

  createStep(
    "738274d6-66f4-11ee-9199-00155db6b9f6",
    paths.preferredName,
    (params, { goTo, onBack }) => {
      return (
        <PreferredNameView
          goTo={goTo}
          onBack={onBack}
          patientId={params.patientId}
          visitId={params.visitId}
        />
      );
    }
  ),

  createStep(
    "36a11b4e-97g4-41ee-3030-00118db3b9f`",
    paths.immediateRiskAssessment,
    (params, { goTo, onBack }) => {
      return (
        <ImmediateRiskAssessmentView
          goTo={goTo}
          onBack={onBack}
          patientId={params.patientId}
          visitId={params.visitId}
        />
      );
    }
  ),

  createStep(
    "6ca73322-66f4-11ee-b079-00155db6b9f6",
    paths.counterSign,
    (params, { goTo, onBack }) => {
      return (
        <StudyCounterSignView
          goTo={goTo}
          onBack={onBack}
          patientId={params.patientId}
          visitId={params.visitId}
        />
      );
    }
  ),

  createStep(
    "8a1c9f3e-2b7d-4c6a-9e5f-0d3b8a7c6e1f",
    paths.extendedVisitBloodDrawIntro,
    (params, { goTo, onBack }) => {
      return (
        <ExtendedVisitBloodDrawIntro
          goTo={goTo}
          onBack={onBack}
          patientId={params.patientId}
          visitId={params.visitId}
        />
      );
    }
  ),

  createStep(
    "3d7e0a1b-6c4f-4a2d-8b9e-1f5c2d9a7b6e",
    paths.extendedVisitLimsBloodDrawConfirm,
    (params, { goTo, onBack }) => {
      return (
        <DrawConfirmationView
          goTo={goTo}
          exit={{ label: "Back", onPress: onBack }}
          doneUrl={paths.extendedVisitBloodDrawOutro.url(params)}
          patientId={params.patientId}
          visitId={params.visitId}
        />
      );
    }
  ),

  createStep(
    "6f2a4c8e-9d1b-4e7a-a3c6-5b8d0e2f4a9c",
    paths.extendedVisitBloodDrawOutro,
    (params, { goTo }) => {
      return (
        <ExtendedVisitBloodDrawOutro
          goTo={goTo}
          patientId={params.patientId}
          visitId={params.visitId}
        />
      );
    }
  ),

  createStep(
    "1c4e6f8a-3b5d-4e9f-8a7c-6b1d0e2f4a9c",
    paths.extendedVisitScanIntro,
    (params, { goTo, onBack }) => {
      return (
        <ExtendedVisitScanIntro
          goTo={goTo}
          onBack={onBack}
          patientId={params.patientId}
          visitId={params.visitId}
        />
      );
    }
  ),

  createStep(
    "9214fbb7-cf2d-4fa4-a5c3-805902d036da",
    paths.assignToRoom,
    (params, { goTo }) => {
      return (
        <AssignToRoomView
          goTo={goTo}
          patientId={params.patientId}
          visitId={params.visitId}
        />
      );
    }
  ),

  createStep(
    "0ca071f9-aaeb-4c39-b29e-badfb416d010",
    paths.assignToSkinScanner,
    (params, { goTo }) => {
      return (
        <AssignToSkinScanner
          goTo={goTo}
          patientId={params.patientId}
          visitId={params.visitId}
        />
      );
    }
  ),

  createStep(
    "96a46898-66f4-11ee-8bdd-00155db6b9f6",
    paths.checkInSuccess,
    (params, { goTo }) => {
      return (
        <CheckInCompleteView
          goTo={goTo}
          patientId={params.patientId}
          visitId={params.visitId}
        />
      );
    }
  ),

  createStep(
    "caa243ac-3293-4997-b036-05d8e761e99c",
    paths.assignToNoravECGDevice,
    (params, { goTo }) => {
      return (
        <AssignToNoravECGDevice
          goTo={goTo}
          patientId={params.patientId}
          visitId={params.visitId}
        />
      );
    }
  ),

  createStep(
    "b7e3a1f2-8c4d-4e9f-a6b5-2d1c0e9f8a7b",
    paths.assignVials,
    (params, { goTo }) => {
      return (
        <AssignVials
          goTo={goTo}
          patientId={params.patientId}
          visitId={params.visitId}
        />
      );
    }
  ),

  createStep(
    "1afc0cd9-b3f5-45b3-a966-4a6de733c1a6",
    paths.drawConfirmation,
    (params, { goTo, onBack }) => {
      return (
        <DrawConfirmationView
          goTo={goTo}
          exit={{ label: "Back", onPress: onBack }}
          doneUrl={paths.checkInSuccess.url(params)}
          patientId={params.patientId}
          visitId={params.visitId}
        />
      );
    }
  ),

  createStep(
    "5c8e1b47-2a6d-4f39-9e80-7b3c1d05a2f6",
    paths.drawConfirmationStep,
    (params, { goTo }) => {
      return (
        <DrawConfirmationView
          goTo={goTo}
          exit={{
            label: "Skip blood draw for now",
            onPress: () => goTo(paths.checkInSuccess.url(params)),
          }}
          doneUrl={paths.checkInSuccess.url(params)}
          patientId={params.patientId}
          visitId={params.visitId}
          requireScanStart
        />
      );
    }
  ),

  createStep(
    "d1e2f3a4-5b6c-7d8e-9f0a-1b2c3d4e5f6a",
    paths.followUpDerma,
    (params, { goTo }) => {
      return (
        <FollowUpDermaView
          goTo={goTo}
          patientId={params.patientId}
          visitId={params.visitId}
        />
      );
    }
  ),

  createStep(
    "e7f3a1b2-8c4d-4e5f-b6a7-2c3d4e5f6a7b",
    paths.followUpEcg,
    (params, { goTo }) => {
      return (
        <FollowUpEcgView
          goTo={goTo}
          patientId={params.patientId}
          visitId={params.visitId}
        />
      );
    }
  ),

  createStep(
    "f8a4b2c3-9d5e-4f6a-c7b8-3d4e5f6a7b8c",
    paths.assignToNoravECGDeviceFollowUp,
    (params, { goTo }) => {
      return (
        <AssignToNoravECGDevice
          goTo={goTo}
          patientId={params.patientId}
          visitId={params.visitId}
          returnPath={paths.followUpEcg.url({
            patientId: params.patientId,
            visitId: params.visitId,
          })}
        />
      );
    }
  ),

  createStep(
    "acd86bd7-4de3-42e0-bbe1-04a18d8a2832",
    paths.nekoLensAuth,
    (params, { onBack }) => {
      return (
        <NekoLensAuthView
          onBack={onBack}
          patientId={params.patientId}
          visitId={params.visitId}
        />
      );
    }
  ),

  createStep(
    "3f7a2b1c-8d4e-4f5a-b6c7-1d2e3f4a5b6c",
    paths.manualCheckInConfirmed,
    (params, { goTo }) => {
      return (
        <ManualCheckInConfirmedView
          goTo={goTo}
          patientId={params.patientId}
          visitId={params.visitId}
        />
      );
    }
  ),

  createStep(
    "a3290b96-66f4-11ee-ab4b-00155db6b9f6",
    paths.checkOut,
    (params, { goTo }) => {
      return (
        <CheckOutView
          goTo={goTo}
          patientId={params.patientId}
          visitId={params.visitId}
        />
      );
    }
  ),

  createStep(
    "b7c41e28-5a93-4d16-9f3b-2c8ea70d4f51",
    paths.checkOutConfirm,
    (params, { replaceWith, onBack }) => {
      return (
        <CheckOutConfirmView
          replaceWith={replaceWith}
          onBack={onBack}
          patientId={params.patientId}
          visitId={params.visitId}
        />
      );
    }
  ),

  createStep(
    "e93f60ad-1b74-4c85-a2d9-6f0b83e1c7a4",
    paths.checkOutConfirmed,
    (params, { goTo }) => {
      return (
        <CheckOutConfirmedView
          goTo={goTo}
          patientId={params.patientId}
          visitId={params.visitId}
        />
      );
    }
  ),
];

export function findStep(pathname: string) {
  return steps.find((step) => {
    // Look for exact match
    return step.path.match(pathname) === 0;
  });
}
